import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../lib/api';
import { fmt$, s } from './reportKit';

// ── Bills — what each open QuickBooks bill really is ──
// The server reads QuickBooks fresh on every load (a few seconds) and sorts
// each open bill (backend lib/billsCheck.js). Nothing here changes QuickBooks:
// Olaf reads it, the bookkeeper makes the fixes.
const GROUP_COLORS = {
  paid: '#2e7d32',
  duplicate: '#b3261e',
  'add-invoice': '#1565c0',
  waiting: '#777',
  'past-due': '#b26a00',
  'not-due': '#555',
};

// QuickBooks dates are calendar days ("2026-09-02"). Read as midnight UTC
// they show as the day before in Colorado, so pin them to midday.
function fmtDay(isoDay) {
  if (!isoDay) return '—';
  return new Date(`${isoDay}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export default function BillsReport() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkedAt, setCheckedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setD(await adminApi.getOpenBills());
      setCheckedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not read QuickBooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const header = (
    <div style={{ ...s.filterBar, justifyContent: 'space-between' }}>
      <p style={s.sub}>
        Every open bill in QuickBooks, sorted by what it most likely is.
        {checkedAt && ` Read from QuickBooks at ${checkedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.`}
      </p>
      <button style={s.btnOutline} onClick={load} disabled={loading}>{loading ? 'Reading QuickBooks…' : 'Check again'}</button>
    </div>
  );

  if (error) return <div>{header}<p style={s.errorText}>{error}</p></div>;
  if (!d) return <div>{header}<p style={s.sub}>Reading QuickBooks…</p></div>;

  const shown = d.groups.filter(g => g.count);
  return (
    <div>
      {header}
      <div style={s.kpiGrid}>
        {d.groups.map(g => (
          <a key={g.key} href={g.count ? `#bills-${g.key}` : undefined} style={{ ...s.kpiCard, textDecoration: 'none', opacity: g.count ? 1 : 0.55 }}>
            <div style={{ ...s.kpiLabel, color: GROUP_COLORS[g.key] }}>{g.label}</div>
            <div style={s.kpiValue}>{fmt$(g.balance)}</div>
            <div style={st.kpiSub}>{g.count} bill{g.count === 1 ? '' : 's'}</div>
          </a>
        ))}
      </div>
      <p style={{ ...s.sub, marginBottom: '1rem' }}>
        {d.count} open bills, {fmt$(d.balance)} in total. {d.withoutPdf} have no invoice PDF attached.
        {' '}“Probably paid” matches vendor, amount and date, so check each one before voiding it. A payment still waiting in the bank feed doesn’t show here.
      </p>
      {shown.map(g => (
        <div key={g.key} id={`bills-${g.key}`} style={s.section}>
          <h3 style={{ ...s.sectionTitle, marginBottom: 4 }}>
            <span style={{ color: GROUP_COLORS[g.key] }}>{g.label}</span> · {g.count} · {fmt$(g.balance)}
          </h3>
          <p style={{ ...s.sub, marginBottom: '0.75rem' }}>{g.action}</p>
          <div style={st.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Vendor and ref</th>
                  <th style={s.th}>Dates</th>
                  <th style={s.thR}>Open</th>
                  <th style={s.th}>What we found</th>
                  <th style={s.th}>Next step</th>
                </tr>
              </thead>
              <tbody>
                {g.bills.map(b => (
                  <tr key={b.id}>
                    <td style={{ ...s.td, ...st.top, minWidth: 170 }}>
                      <div style={{ fontWeight: 600 }}>{b.vendor}</div>
                      <div style={st.dim}>{b.ref || 'no ref'}{b.hasPdf ? ' · PDF attached' : ''}</div>
                    </td>
                    <td style={{ ...s.td, ...st.top, whiteSpace: 'nowrap' }}>
                      <div>{fmtDay(b.txnDate)}</div>
                      <div style={{ ...st.dim, color: b.daysPastDue ? '#b26a00' : st.dim.color }}>
                        {b.dueDate ? `due ${fmtDay(b.dueDate)}` : 'no due date'}
                      </div>
                    </td>
                    <td style={{ ...s.tdR, ...st.top, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{fmt$(b.balance)}</div>
                      {b.balance !== b.total && <div style={st.dim}>of {fmt$(b.total)}</div>}
                    </td>
                    <td style={{ ...s.td, ...st.top, minWidth: 260 }}>{b.found.join(' ')}</td>
                    <td style={{ ...s.td, ...st.top, minWidth: 200, color: '#A50B18' }}>{b.next}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

const st = {
  kpiSub: { fontSize: '0.8rem', color: '#888', marginTop: 4 },
  scroll: { width: '100%', overflowX: 'auto' },
  top: { verticalAlign: 'top' },
  dim: { color: '#999', fontSize: '0.78rem', marginTop: 2 },
};
